import { css } from "@emotion/css";
import React from "react";
import { AriaSwitchProps, useFocusRing, useSwitch } from "react-aria";
import { useToggleState } from "react-stately";

import { ToggleSwitch } from "./toggle-switch/ToggleSwitch";
import { Stack } from "../../../foundations/grid-layout/Stack";
import { Text } from "../../../foundations/typography/Text";
import { InputCard } from "../_assets/card/InputCard";
import { FormText } from "../_assets/input-wrapper/FormText";
export interface ToggleProps extends AriaSwitchProps {
  subtle?: boolean;
  description?: string;
  helpText?: string;
  card?: boolean;
  testID?: string;
}
interface ConditionalWrapperProps {
  children: React.ReactNode;
  card?: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  testID?: string;
}

const ConditionalWrapper = (props: ConditionalWrapperProps) => {
  const { card, children, isChecked, isDisabled, testID } = props;
  if (card)
    return (
      <InputCard isChecked={isChecked} isDisabled={isDisabled} testID={testID}>
        {children}
      </InputCard>
    );
  else
    return (
      <label
        className={css({
          display: "inline-flex",
          paddingTop: "var(--space-1)",
          paddingBottom: "var(--space-1)",
        })}
        data-testid={`${testID}-label`}
      >
        {children}
      </label>
    );
};

export function Toggle(props: ToggleProps) {
  const {
    subtle,
    description,
    helpText,
    children,
    card,
    testID = "toggle",
  } = props;
  const state = useToggleState(props);
  const ref = React.useRef<HTMLInputElement>(null);
  const { inputProps } = useSwitch(props, state, ref);
  const { focusProps } = useFocusRing();
  const isChecked = inputProps.checked;
  const isDisabled = inputProps.disabled;

  return (
    <ConditionalWrapper
      card={card}
      isChecked={isChecked}
      isDisabled={isDisabled}
      testID={`${testID}-wrapper`}
    >
      <ToggleSwitch
        card={card}
        focusProps={focusProps}
        testID={`${testID}-toggle`}
        {...inputProps}
      />
      <div
        className={css(styles.textContent, {
          paddingLeft: !card ? "var(--space-2)" : undefined,
          gap: !card ? "var(--space-1)" : undefined,
        })}
      >
        <Text
          style={subtle ? "input/helper" : "input/label"}
          color={subtle ? "text-weak" : "text-base"}
          className={styles.labelText}
        >
          {children}
        </Text>
        {description && (
          <Stack gap="space-4">
            <FormText className={styles.description}>{description}</FormText>
            {helpText && (
              <Text style="global/info-label" color="text-weak">
                {helpText}
              </Text>
            )}
          </Stack>
        )}
      </div>
    </ConditionalWrapper>
  );
}

const styles = {
  labelText: css({
    padding: 0,
    margin: 0,
  }),
  description: css({
    padding: 0,
    margin: 0,
  }),
  textContent: css({
    flexDirection: "column",
    display: "flex",
  }),
};
